/**
 * orisige: お届け先を選ぶと、その場で送料が出る計算機。
 *
 * ## なぜ必要か
 * 送料が購入手続きまで分からないことは、カゴ落ち理由の最上位（Baymard: 48%）。
 * 一方、金額を本文にベタ書きすると料率を変えるたびに原稿修正が要る。
 *
 * ## どう解決しているか
 * Shopify の AJAX API `/cart/shipping_rates.json` を叩いて **その時点の実料率** を取る。
 * 金額はテーマのどこにもハードコードしない。管理画面の配送設定だけが唯一の出どころで、
 * そこを直せば商品ページもカートも自動で追従する。
 *
 * ## 前提と対処
 * ⚠ このエンドポイントは **カートに商品が入っていること** が前提。空だと [] が返る。
 *   商品ページは「カートに入れる前」に見たい場所なので、空のときだけ裏で1点入れて、
 *   取得後に必ず戻す。目印（TEMP_PROPERTY）を付けてお客様の行と混ざらないようにし、
 *   返しそこねた場合は次の表示で掃除する（#sweepBorrowedLines）。
 * ⚠ 返るのは最小通貨単位の **文字列**（JPY なら「678」= 678円）。money フィルタは通せない。
 * ⚠ 返る name は配送レートの名前（＝管理画面でつけた「関東」等のゾーン名）。ここも店の設定が出どころ。
 *
 * 都道府県と郵便番号の一覧は **Liquid 側**（snippets/orisige-shipping-estimator.liquid）にある。
 * カートの morph で巻き戻らないようにするためで、こちらは option の data-zip を読むだけ。
 */

/** 選んだ都道府県は覚えておく。商品ページ→カートと回っても選び直させない */
const STORAGE_KEY = 'orisige:shipping-province';

/** 見積もり用に一時追加した行の目印。客が入れた同じ商品の行と統合されないようにする */
const TEMP_PROPERTY = '_orisige_shipping_estimate';

/**
 * 「いま1点借りている」印。返し終えたら消す。
 *
 * ⚠ これが残ったままページが読まれたら、前回の返却が最後まで走らなかったということ
 *   （見積もりの最中にリンクを踏んだ、タブを閉じた等）。放っておくと**お客様が買った
 *   覚えのない商品がカートに残り、そのまま購入されうる**ので、次の表示で必ず掃除する。
 *
 * ⚠ sessionStorage ではなく localStorage に置くこと。タブを閉じて数日後に戻ってきた
 *   ときこそ掃除が要るのに、sessionStorage だと印ごと消えて取り残しに気づけない。
 */
const BORROW_FLAG = 'orisige:shipping-borrowing';

const root = () => window.Shopify?.routes?.root ?? '/';

/** localStorage / sessionStorage はプライベートモード等で読み書きが例外を投げる */
const store = (storage, key) => ({
  get() {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  },
  set(value) {
    try {
      storage.setItem(key, value);
    } catch {
      // 覚えられなくても見積もり自体は動く
    }
  },
  clear() {
    try {
      storage.removeItem(key);
    } catch {
      // 同上
    }
  },
});

const province = store(localStorage, STORAGE_KEY);
const borrowing = store(localStorage, BORROW_FLAG);

/**
 * 通貨記号はレスポンス側（rate.currency）から取る。ここで 'JPY' を決め打ちしない。
 *
 * ⚠ ja-JP の Intl は narrowSymbol を指定しても **全角の ￥（U+FFE5）** を返す。
 *   一方テーマ本体の money フィルタは **半角の ¥（U+00A5）** なので、そのままだと
 *   同じ画面で商品価格「¥4,000」と送料「￥1,445」が併存して字面がぶれる。
 *   見た目の統一のために最後で半角へ寄せている。
 */
const money = (value, currency) => {
  const amount = Number(value);
  let formatted;
  try {
    formatted = new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // 未知の通貨コード、または narrowSymbol 非対応の古いブラウザ
    formatted = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(amount);
  }
  return formatted.replace('￥', '¥');
};

const json = (path, options) =>
  fetch(`${root()}${path}`, {
    ...options,
    headers: { Accept: 'application/json', ...(options?.body && { 'Content-Type': 'application/json' }), ...options?.headers },
  });

class ShippingEstimator extends HTMLElement {
  /** 選択を素早く変えたとき、古いレスポンスで上書きされないようにする通し番号 */
  #requestId = 0;

  /** 付けたリスナをまとめて外すため。再接続で二重に張るのも防げる */
  #listeners = null;

  /** 中身が遅れて入ってくる場合の見張り。初期化できたら止める */
  #pending = null;

  /** morph に表示を巻き戻されたら描き直すための見張り。つないでいる間ずっと動く */
  #watcher = null;

  /** 数量の ± 連打で借用と返却を何度も往復させないための間引き */
  #debounce = null;

  connectedCallback() {
    if (this.#init()) return;

    /*
     * ⚠ カートドロワーは Horizon の morph で差し替わる。そのとき**この要素が先に
     *   document へ入り、中身が後から入る**ことがある。素直に connectedCallback で
     *   querySelector すると null を掴み、黙って何もしない部品になる。
     *   何フレーム後に揃うかは morph の量しだいなので、回数で打ち切らず中身を見張る。
     */
    this.#pending = new MutationObserver(() => {
      if (this.#init()) {
        this.#pending?.disconnect();
        this.#pending = null;
      }
    });
    this.#pending.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    clearTimeout(this.#debounce);
    this.#listeners?.abort();
    this.#listeners = null;
    this.#pending?.disconnect();
    this.#pending = null;
    this.#watcher?.disconnect();
    this.#watcher = null;
  }

  /**
   * 中身が揃っていれば初期化する。
   * @returns {boolean} 初期化できたか（できなければ呼び出し元が待つ）
   */
  #init() {
    const select = this.querySelector('[data-pref-select]');
    const result = this.querySelector('[data-result]');
    if (!select || !result) return false;

    this.select = select;
    this.result = result;

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    select.addEventListener('change', this.#handleChange, { signal });

    // 数量や品目が変われば、重量・金額で決まる料率の店では送料も変わる。
    // この店は現状ゾーン固定だが、管理画面で料率の決め方を変えられても追従できるようにしておく。
    if (this.dataset.context === 'cart') {
      document.addEventListener('shopify:cart:lines-update', this.#handleCartUpdate, { signal });
    }

    if (this.dataset.context === 'product') {
      // 数量を変えたら、その数量で測り直す（3点買うつもりなら3点ぶんの送料）
      document.addEventListener('quantity-selector:update', this.#handleQuantityChange, { signal });

      /*
       * バリアントを変えたときも測り直す。いまは全バリアント同じ料金だが、
       * 商品に重量を設定すれば「まじめ 200g / おっとり 400g」のように変わりうる。
       *
       * ⚠ Horizon のバリアント切替は**セクション全体を差し替えない**（既定では
       *   バリアントピッカーだけを morph する）。だから data-state を見ている
       *   #watcher では拾えず、**表示が前のバリアントのまま残る**。実際そうなっていた。
       *
       * ⚠ closest のセクションで受けること。document で受けると、おすすめ商品の
       *   カード（クイック追加）の切替にも反応してしまう。
       */
      this.closest('.shopify-section, dialog')?.addEventListener(
        'shopify:product:select',
        this.#handleVariantChange,
        { signal }
      );
    }

    /*
     * カートの数量を変えた、商品のバリアントを変えた——そのたびに Horizon は
     * この部品を含むセクションを morph で描き直し、**出していた送料を消す**
     * （data-state 属性も結果の中身もサーバー側の初期状態へ巻き戻る）。
     *
     * ⚠ shopify:cart:lines-update を聞いて描き直す作りにすると、
     *   「描く」と「morph が消す」の順番が運まかせになり、消されたまま残ることがある
     *   （実際それでカート更新後に送料が空になった）。
     *   そこで**消されたこと自体を見て**描き直す。順番に依存しないぶん確実で、
     *   カート更新・バリアント変更のどちらも同じ道で拾える。
     *
     * 描き直すと data-state が戻るので、この見張りは自分の書き込みでは再発火しない。
     */
    this.#watcher?.disconnect();
    this.#watcher = new MutationObserver(() => {
      if (this.select?.value && !this.dataset.state) this.#estimate();
    });
    this.#watcher.observe(this, { attributes: true, attributeFilter: ['data-state'] });

    // 前回の返却が終わっていなければ、何よりも先に取り消す。
    // data-stray はカート側で Liquid が見つけた取り残し（印が失われていても拾える）。
    if (borrowing.get() || this.dataset.stray) this.#sweepBorrowedLines();

    this.#restore();
    return true;
  }

  /**
   * 前回選んだ都道府県を選び直しておく。あれば続けて見積もる。
   *
   * ⚠ カートでだけ行う。商品ページで自動で見積もらないこと。
   *   商品ページの見積もりは #borrowVariant が cart/add.js を叩く。Shopify が全ページに
   *   読み込む shop_events_listener はこの fetch を検出して「カートに追加」を計測するので、
   *   以前は**再訪問者が商品ページを開くだけで**カート追加が1回計上されていた
   *   （Shopify Analytics・GA4 add_to_cart・Meta AddToCart がすべて水増し）。
   *   商品ページではお客様が都道府県を選んだときにだけ計算する。
   *   カート側は借用しないのでイベントは出ない。
   */
  #restore() {
    if (this.dataset.context !== 'cart') return false;

    const saved = province.get();
    if (!saved) return false;

    const option = this.select.querySelector(`option[value="${CSS.escape(saved)}"]`);
    if (!option) return false;

    this.select.value = saved;
    this.#estimate();
    return true;
  }

  /**
   * カートが変わったら取り直す。
   * ⚠ 描いた直後に morph が消しにくることがあるが、その取りこぼしは下の #watcher が拾う。
   *   ここでは順番を気にせず、素直に最新を取りにいくだけでよい。
   */
  #handleCartUpdate = () => {
    if (this.select?.value) this.#estimate();
  };

  /**
   * バリアントの変更。
   * @param {Event & {promise?: Promise<unknown>}} event
   */
  #handleVariantChange = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    /*
     * 差し替えが終わるのを待ってから測る。フォームの [name="id"] を新しい ID に
     * 書き換えるのは Horizon 側（product-form.js）で、同じ promise にぶら下がっている。
     * どちらが先に走るかは登録順しだいなので、待ったうえで #handleQuantityChange の
     * 400ms を挟み、**新しい ID が入ってから**借用するようにしている。
     */
    const rerun = () => this.#handleQuantityChange();
    if (event.promise?.then) event.promise.then(rerun, rerun);
    else rerun();
  };

  /**
   * 数量の変更。± を連打されると借用と返却を往復し続けるので少し待つ。
   * ⚠ 表示の追い越しは #requestId が防ぐが、カートへの出し入れは実際に走ってしまう。
   *   間引きはその往復を減らすためのもので、正しさのためではない。
   */
  #handleQuantityChange = () => {
    if (!this.select?.value) return;
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.isConnected && this.#estimate(), 400);
  };

  #handleChange = () => {
    if (!this.select.value) {
      province.clear();
      this.#render(null);
      return;
    }
    province.set(this.select.value);
    /*
     * キーボードの↑↓で選び直すと change が1段ごとに飛び、そのたびに実カートへ
     * add/change が走っていた（5回で16リクエストを実測）。数量と同じく少し待つ。
     */
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.isConnected && this.#estimate(), 400);
  };

  /**
   * 送料を取り直して描く。
   *
   * ## 何を見積もっているか（複数点を買うときの扱い）
   * この API は**カート全体**に対して料率を返す。だから見積もりの正しさは
   * 「見積もった瞬間のカートの中身」で決まる。
   *
   * - カート側（ドロワー／/cart）… 実物のカートをそのまま見るので、何点入っていようと正しい。
   * - 商品ページ側 … **いまのカート + この商品** の状態を作ってから見積もる。
   *
   * ⚠ 以前は「カートが空のときだけ1点借りる」だった。いまの折重の送料は都道府県だけで
   *   決まる完全な定額（16点・¥103,840 でも ¥678 で実測確認）なので数字は同じだったが、
   *   **構造として間違っていた**。「¥10,000以上送料無料」のような条件を管理画面で足した
   *   瞬間に、見ている商品を含まない金額を出し続け、しかもエラーは出ない。
   *   金額を管理画面だけで決められる状態を守るため、常に借りて正しい状態で測る。
   */
  async #estimate() {
    const option = this.select.selectedOptions[0];
    if (!option?.value) return;

    const token = ++this.#requestId;
    this.#render({ state: 'loading' });

    let borrowedLineKey = null;
    try {
      if (this.dataset.context === 'product') {
        borrowedLineKey = await this.#borrowVariant();
        // 品切れのバリアント等でカートに入れられなかった。嘘の金額を出すより黙る
        if (!borrowedLineKey) {
          if (token === this.#requestId) this.#render({ state: 'unavailable' });
          return;
        }
      }

      const query = new URLSearchParams({
        'shipping_address[country]': 'Japan',
        'shipping_address[province]': option.value,
        // Shopify が必須にしているだけの詰め物。料金は province のほうで決まる
        'shipping_address[zip]': option.dataset.zip ?? '',
      });
      const response = await json(`cart/shipping_rates.json?${query}`);
      if (!response.ok) throw new Error(`shipping_rates ${response.status}`);

      const { shipping_rates: rates } = await response.json();
      if (token !== this.#requestId) return; // もっと新しい選択が走っている
      this.#render(rates?.length ? { state: 'rates', rates } : { state: 'empty' });
    } catch {
      if (token === this.#requestId) this.#render({ state: 'error' });
    } finally {
      if (borrowedLineKey) await this.#returnVariant(borrowedLineKey);
    }
  }

  /**
   * 「カートに追加する」を押したときと同じものを、一時的にカートへ入れる。
   * バリアントも数量もフォームの現在値を読むので、3点買うつもりなら3点で見積もる。
   * @returns {Promise<string|null>} 行キー（入れられなければ null）
   */
  async #borrowVariant() {
    const form = document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    // バリアントピッカーで切り替わるので、属性ではなくフォームの現在値を都度読む
    const variantId = form?.querySelector('[name="id"]')?.value || this.dataset.variantId;
    if (!variantId) return null;

    const quantity = Number(form?.querySelector('[name="quantity"]')?.value) || 1;

    // 追加する**前**に印を置く。応答を待つ間に離脱されても掃除できるようにするため
    borrowing.set('1');

    const response = await json('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity, properties: { [TEMP_PROPERTY]: 'true' } }],
      }),
    });
    if (!response.ok) {
      /*
       * ⚠ 失敗しても**入っていることがある**。在庫3点の商品に10点を頼むと
       *   422 「出品状況により、アイテム3点のみがカートに追加されました」が返り、
       *   カートには3点の行ができている（実測）。
       *   ここで印だけ消すと、その3点が取り残しとして残る。印は消さず、その場で掃除する。
       */
      await this.#sweepBorrowedLines();
      return null;
    }

    const data = await response.json();
    return data?.items?.[0]?.key ?? null;
  }

  /**
   * 借りた1点を返す。
   * keepalive を付けているのは、見積もりの途中でリンクを踏まれても
   * この取り消しだけはブラウザに最後まで送り切ってもらうため。
   */
  async #returnVariant(key) {
    try {
      await json('cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: 0 }),
        keepalive: true,
      });
      borrowing.clear();
    } catch {
      // 送り切れなかった。印は残したままにして、次にこの部品が表示されたとき掃除させる
    }
  }

  /**
   * 前回返しそこねた「見積もり用の1点」をカートから消す。
   * 目印の付いた行だけを狙うので、お客様がご自分で入れた同じ商品には触れない。
   */
  async #sweepBorrowedLines() {
    try {
      const cart = await (await json('cart.js')).json();
      const strays = (cart.items ?? []).filter((item) => item.properties?.[TEMP_PROPERTY]);

      if (!strays.length) {
        borrowing.clear();
        return;
      }

      let latest = cart;
      for (const stray of strays) {
        const response = await json('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: stray.key, quantity: 0 }),
        });
        if (response.ok) latest = await response.json();
      }
      borrowing.clear();
      this.#syncCartCount(latest.item_count ?? 0);
    } catch {
      // 消せなければ印を残す。次の表示でもう一度試みる
    }
  }

  /**
   * ヘッダーのカート個数バッジを実際の数へ合わせる。
   *
   * ⚠ cart-icon（Horizon標準）は sessionStorage['cart-count'] を10秒だけ信じて
   *   描画済みの数を上書きする、という内部実装に依存している。
   *   将来この仕組みが変わったら、バッジの更新が次の遷移まで遅れるだけで実害はない。
   */
  #syncCartCount(count) {
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch {
      return;
    }
    document.querySelector('cart-icon')?.ensureCartBubbleIsCorrect?.();
  }

  /**
   * @param {{state: 'loading'|'error'|'empty'|'rates', rates?: {name: string, price: string, currency: string}[]} | null} payload
   */
  #render(payload) {
    // morph が結果欄のノードを差し替えている場合があるので、書く直前に取り直す
    this.result = this.querySelector('[data-result]') ?? this.result;

    if (!payload) {
      this.result.hidden = true;
      this.result.replaceChildren();
      this.removeAttribute('data-state');
      return;
    }

    this.result.hidden = false;
    this.dataset.state = payload.state;

    if (payload.state !== 'rates') {
      const messages = {
        loading: '計算しています…',
        error: '送料を取得できませんでした。ご購入手続きの際に表示されます。',
        empty: 'この地域へのお届け方法が見つかりませんでした。お問い合わせください。',
        unavailable: 'この商品の送料は、ご購入手続きの画面でご確認ください。',
      };
      const note = document.createElement('span');
      note.className = 'orisige-shipping__note';
      note.textContent = messages[payload.state];
      this.result.replaceChildren(note);
      return;
    }

    // 配送方法が複数設定されている店舗もあるので、安い順に全部出す
    const rows = [...payload.rates]
      .sort((a, b) => Number(a.price) - Number(b.price))
      .map(({ name, price, currency }) => {
        const row = document.createElement('span');
        row.className = 'orisige-shipping__rate';

        const label = document.createElement('span');
        label.className = 'orisige-shipping__rate-name';
        label.textContent = name;

        const amount = document.createElement('span');
        amount.className = 'orisige-shipping__rate-price';
        amount.textContent = money(price, currency);

        row.append(label, amount);
        return row;
      });

    this.result.replaceChildren(...rows);
  }
}

if (!customElements.get('orisige-shipping-estimator')) {
  customElements.define('orisige-shipping-estimator', ShippingEstimator);
}
